'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { knowledgeBaseApi, type ActivityItem } from '../_lib/api';
import { MessageSquare, AtSign, ThumbsUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KBActivityFeedProps {
  className?: string;
}

type FilterType = 'all' | 'mentions' | 'comments';

function getActivityIcon(type: string) {
  switch (type) {
    case 'mention':
      return <AtSign className="h-4 w-4 text-purple-500" />;
    case 'comment':
      return <MessageSquare className="h-4 w-4 text-blue-500" />;
    case 'like':
      return <ThumbsUp className="h-4 w-4 text-green-500" />;
    default:
      return <MessageSquare className="h-4 w-4 text-gray-400" />;
  }
}

export function KBActivityFeed({ className = '' }: KBActivityFeedProps) {
  const { t, locale } = useTranslation();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMockData, setIsMockData] = useState(false); // 标记是否使用 mock 数据

  const formatNumber = (value: number) => new Intl.NumberFormat(locale).format(value);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.knowledgeBase.time.justNow;
    if (diffMins < 60) {
      return t.knowledgeBase.time.minutesAgo.replace('{count}', formatNumber(diffMins));
    }
    if (diffHours < 24) {
      return t.knowledgeBase.time.hoursAgo.replace('{count}', formatNumber(diffHours));
    }
    if (diffDays === 1) return t.knowledgeBase.time.yesterday;
    return t.knowledgeBase.time.daysAgo.replace('{count}', formatNumber(diffDays));
  };

  const getActivityDescription = (item: ActivityItem): string => {
    switch (item.type) {
      case 'mention':
        return t.knowledgeBase.activity.description.mention;
      case 'comment':
        return t.knowledgeBase.activity.description.comment;
      case 'like':
        return t.knowledgeBase.activity.description.like;
      default:
        return '';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [activityRes, unreadRes] = await Promise.all([
          knowledgeBaseApi.getActivityFeed(filter, 10),
          knowledgeBaseApi.getUnreadMentionCount(),
        ]);
        // 只在真实数据返回空时显示空状态，不再使用 mock 数据
        setItems(activityRes.items);
        setUnreadCount(unreadRes.count);
        setIsMockData(false);
      } catch (err) {
        console.error('Failed to fetch activity:', err);
        // API 错误时显示空状态，不使用 mock 数据
        setItems([]);
        setUnreadCount(0);
        setIsMockData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter]);

  const handleMarkAsRead = async (id: string) => {
    // 如果是 mock 数据，只更新本地状态，不调用 API
    if (isMockData) {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isRead: true } : item)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return;
    }

    try {
      await knowledgeBaseApi.markMentionAsRead(id);
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isRead: true } : item)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const filters: { key: FilterType; label: string; count?: number }[] = [
    { key: 'all', label: t.knowledgeBase.activity.all },
    { key: 'mentions', label: t.knowledgeBase.activity.mentions, count: unreadCount },
    { key: 'comments', label: t.knowledgeBase.activity.comments },
  ];

  return (
    <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">{t.knowledgeBase.activity.title}</h2>
          <span className="text-xs text-gray-400">
            {t.knowledgeBase.activity.lastUpdated.replace('{time}', t.knowledgeBase.time.justNow)}
          </span>
        </div>
        <div className="mt-3 flex gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                filter === f.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              {f.label}
              {f.count !== undefined && f.count > 0 && (
                <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] text-white">
                  {formatNumber(f.count)}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">{t.knowledgeBase.activity.noActivity}</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'flex gap-3 px-4 py-3 transition-colors hover:bg-gray-50',
                  item.type === 'mention' && !item.isRead && 'bg-blue-50/50',
                )}
                onClick={() => {
                  if (item.type === 'mention' && !item.isRead) {
                    handleMarkAsRead(item.id);
                  }
                }}
              >
                <div className="mt-0.5">{getActivityIcon(item.type)}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{item.actorName}</span>{' '}
                    <span className="text-gray-600">{getActivityDescription(item)}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {formatTimeAgo(item.createdAt)} &middot; {item.targetTitle}
                  </p>
                  {item.content && (
                    <p className="mt-1 line-clamp-2 text-xs text-gray-500">{item.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
